import { useRef } from "react";
import { useRevealAll } from "../hooks/useReveal";

export default function Philosophy() {
  const ref = useRef(null);
  useRevealAll(ref);

  return (
    <section
      ref={ref}
      data-testid="philosophy"
      className="py-28 md:py-40 bg-[color:var(--he-bg)] relative overflow-hidden"
    >
      {/* Decorative giant figure */}
      <div className="absolute right-0 -top-12 hidden md:block opacity-[0.04] pointer-events-none select-none">
        <span className="figure italic text-[26rem] leading-none text-[color:var(--he-ink)]">
          02
        </span>
      </div>

      <div className="he-container relative">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-4 reveal">
            <p className="overline mb-6 mag-marker">
              <span>Filozófia</span>
            </p>
            <span className="he-divider" />
          </div>
          <div className="md:col-span-7 md:col-start-6 reveal delay-1">
            <p className="text-[color:var(--he-ink-soft)] font-light text-base md:text-lg leading-[1.7] max-w-xl">
              Egy célunk van: valódi, jó minőségű emberi hajat kínálni azoknak,
              akik komolyan veszik a hajuk megjelenését. Nincs rejtett összetevő,
              nincs félrevezető marketing.
            </p>
          </div>
        </div>

        {/* Magazine spread: image + headline */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-24 md:mb-36 items-end">
          {/* Image column with caption */}
          <div className="md:col-span-5 reveal">
            <div className="he-img-wrap aspect-[4/5]">
              <img
                src="/brand/philosophy.jpg"
                alt="Hair Elixir — Filozófia"
                className="he-img w-full h-full object-cover"
              />
              {/* Floating frame number */}
              <span className="absolute top-5 left-5 figure italic text-white text-3xl">
                Pl. 01
              </span>
            </div>
            {/* Image caption */}
            <div className="mt-5 flex items-start gap-4">
              <span className="block w-8 h-px bg-[color:var(--he-gold)] mt-3 shrink-0" />
              <p className="text-xs text-[color:var(--he-ink-soft)] uppercase font-light tracking-[0.18em] leading-relaxed">
                Természetes mozgás · <span className="italic font-serif-display normal-case text-sm tracking-normal text-[color:var(--he-ink)]">a saját hajadként viselkedik</span>
              </p>
            </div>
          </div>

          {/* Headline column */}
          <div className="md:col-span-7 reveal delay-2">
            <h2
              className="font-serif-display leading-[1.06] tracking-[-0.018em]"
              style={{ fontWeight: 400, fontSize: "clamp(1.8rem, 4.4vw, 4rem)" }}
            >
              100%-os visszavásárlási <br />
              garanciát biztosítunk <br />
              <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                elégedetlen vásárlás esetén.
              </span>
            </h2>

            {/* Split paragraphs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6 mt-12 max-w-2xl">
              <p className="dropcap text-[color:var(--he-ink-soft)] font-light leading-[1.85] text-[15px]">
                Minden copf egyfejről származó, prémium minőségű póthaj —
                kiemelt figyelemmel válogatva, a maximális minőségre
                törekedve.
              </p>
              <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.85] text-[15px] sm:pt-1">
                Célunk minden vásárló elégedettsége, és hogy olyan minőséget
                képviseljünk a póthajpiacon, ami egyedülálló.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-14 flex items-center gap-5">
              <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
              <p
                className="font-serif-display italic text-2xl text-[color:var(--he-gold)]"
                style={{ fontWeight: 400 }}
              >
                Hair Elixir
              </p>
              <span className="overline-xs text-[color:var(--he-ink-soft)]">
                Kecskemét
              </span>
            </div>
          </div>
        </div>

        {/* Three pillars strip */}
        <div className="border-t border-[color:var(--he-line)] pt-16 md:pt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-20">
            <Pillar
              num="01"
              title="Eredet"
              text="Európai és Kazahsztáni régiókból személyesen, egyenként válogatva szerzünk be minden terméket — a maximális minőségre törekedve."
              meta="Két forrás"
              delay="0"
            />
            <Pillar
              num="02"
              title="Struktúra"
              text="Európai vékonyszálú és Kazahsztáni középvastagszálú copfok változatos struktúrában — egyenes, hullámos, göndör — hogy mindenki megtalálja a számára megfelelőt."
              meta="Vékony és középvastag"
              delay="1"
            />
            <Pillar
              num="03"
              title="Tartósság"
              text="Megfelelő ápolással évekig hordható, festhető, melegre formázható, többször újrahasználható."
              meta="2+ év viselés"
              delay="2"
            />
          </div>
        </div>

        {/* Bottom signature stat strip */}
        <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 border-y border-[color:var(--he-line)] py-12">
          <Stat figure="100" suffix="%" label="Valódi emberi haj" />
          <Stat figure="11" label="Aktuális tételek" />
          <Stat figure="55–68" suffix=" cm" label="Hosszválaszték" />
          <Stat figure="2+" suffix=" év" label="Tipikus viselés" italic />
        </div>
      </div>
    </section>
  );
}

function Pillar({ num, title, text, meta, delay }) {
  return (
    <div className={`reveal delay-${delay} group`}>
      <div className="flex items-baseline justify-between border-b border-[color:var(--he-line)] pb-4 mb-6 group-hover:border-[color:var(--he-gold)] transition-colors duration-500">
        <span className="figure italic text-[color:var(--he-gold)] text-4xl">
          {num}
        </span>
        <span className="overline-xs text-[color:var(--he-ink-soft)]">{meta}</span>
      </div>
      <h3
        className="font-serif-display text-3xl md:text-4xl leading-tight mb-4"
        style={{ fontWeight: 400 }}
      >
        {title}
      </h3>
      <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.75] text-[15px] max-w-sm">
        {text}
      </p>
    </div>
  );
}

function Stat({ figure, suffix, label, italic }) {
  return (
    <div className="text-center md:text-left">
      <p
        className={`font-serif-display text-5xl md:text-6xl leading-none ${italic ? "italic text-[color:var(--he-gold)]" : "text-[color:var(--he-ink)]"}`}
        style={{ fontWeight: 300 }}
      >
        {figure}
        {suffix && <span className="text-3xl ml-1">{suffix}</span>}
      </p>
      <p className="overline-xs text-[color:var(--he-ink-soft)] mt-4">{label}</p>
    </div>
  );
}
